import styles from './page.module.css';
import React, { useEffect, useMemo, useState } from 'react';

import axios from 'axios';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { ports } from './ports';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import LayoutUsuario from '@/components/navegacao/LayoutUsuario';
import { useAppliance } from '@/lib/useAppliance';

type PageProps = {};

const labelBytes = (bytes: number) => {
  const q = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  let i = 0;
  let n = bytes;
  while (n > 1024) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(2)} ${q[i]}`;
};

// Converte kbps para a unidade mais legível (kbps → Mbps → Gbps)
const labelKbps = (kbps: number) => {
  const abs = Math.abs(kbps);
  if (abs >= 1_000_000) return `${(kbps / 1_000_000).toFixed(2)} Gbps`;
  if (abs >= 1_000) return `${(kbps / 1_000).toFixed(2)} Mbps`;
  return `${kbps.toFixed(2)} kbps`;
};

// Retorna unidade e divisor para o eixo Y dado o valor máximo em kbps
const kbpsAxisUnit = (maxKbps: number): { unit: string; divisor: number } => {
  if (maxKbps >= 1_000_000) return { unit: 'Gbps', divisor: 1_000_000 };
  if (maxKbps >= 1_000) return { unit: 'Mbps', divisor: 1_000 };
  return { unit: 'kbps', divisor: 1 };
};

const TOP_PORTS = 10;

const chartColors = [
  'var(--color-data)',
  'var(--tc-color-info)',
  'var(--status-online)',
  'var(--status-offline)',
  'var(--tc-color-warning)',
  'var(--webip-mint-600)',
  'var(--webip-navy-300)',
  'var(--fg-2)',
];

const topPortsWithOthers = (data: any[]): any[] => {
  // Um "outros" pode já vir dos dados (sentinela -1 do rollup); ele nunca
  // disputa o top — vai pro mesmo balde que o resto, senão o donut mostra
  // duas fatias "outros".
  const sorted = [...data].filter(i => i.port !== 'outros').sort((a, b) => b.sum_bytes - a.sum_bytes);
  const top = sorted.slice(0, TOP_PORTS);
  const rest = [...sorted.slice(TOP_PORTS), ...data.filter(i => i.port === 'outros')];
  if (rest.length === 0) return top;
  const outros = rest.reduce(
    (acc, item) => ({
      port: 'outros',
      sum_bytes: acc.sum_bytes + item.sum_bytes,
      sum_bytes_download: acc.sum_bytes_download + item.sum_bytes_download,
      sum_bytes_upload: acc.sum_bytes_upload + item.sum_bytes_upload,
      percentage: 0,
    }),
    { port: 'outros', sum_bytes: 0, sum_bytes_download: 0, sum_bytes_upload: 0, percentage: 0 },
  );
  const total = [...top, outros].reduce((s, i) => s + i.sum_bytes, 0);
  return [...top, outros].map(i => ({ ...i, percentage: +((i.sum_bytes / total) * 100).toFixed(1) }));
};

const processFlowData = (rawData: any[], lans: string[]): any[] => {
  const result = rawData.reduce((acc: Record<string, any>, item: any) => {
    if (!acc[item.datetime_bin]) {
      acc[item.datetime_bin] = { datetime_bin: item.datetime_bin };
    }
    const it = acc[item.datetime_bin];
    const isLan =
      lans.includes(item.source_interface) ||
      lans.includes(item.destination_interface);
    const network = isLan ? 'LAN' : 'WAN';
    if (!it[network]) {
      it[network] = { upload: 0, download: 0 };
    }
    it[network].upload += Math.abs(+item.avg_kbps_upload);
    it[network].download += Math.abs(+item.avg_kbps_download);
    return acc;
  }, {});
  // O backend devolve ORDER BY datetime_bin DESC (o LIMIT precisa recortar os
  // bins mais antigos, não os mais novos); o gráfico de linha lê da esquerda
  // pra direita, então o tempo tem que crescer nessa direção.
  return Object.values(result).reverse();
};

const Page = ({ }: PageProps) => {
  const [data, setData] = useState([]);
  const [dataByAsn, setDataByAsn] = useState([]);
  const [dataByDev, setDataByDev] = useState<any[]>([]);
  const [dataByDevSrc, setDataByDevSrc] = useState([]);
  // const [dataByDevDst, setDataByDevDst] = useState([]);
  const [dataLanByPort, setDataLanByPort] = useState([]);

  const [interfaces, setInterfaces] = useState<any[]>([]);
  const [rawFlowData, setRawFlowData] = useState<any[]>([]);

  const lanInterfaces = useMemo(
    () => interfaces?.filter(it => it.type === 'lan')?.map(it => it.name)?.filter((v, i, a) => a.indexOf(v) === i),
    [interfaces],
  );

  const routerParams = useParams()
  const navigate = useNavigate()
  const { applianceOptions } = useAppliance()

  // selectedId: controlled by local state so the user can override the URL param
  const [selectedId, setSelectedId] = useState<string>(routerParams.id ?? '')

  const apiPath = selectedId ? '/' + selectedId : ''

  useEffect(() => {
    setData(processFlowData(rawFlowData, lanInterfaces ?? []));
  }, [rawFlowData, lanInterfaces]);

  useEffect(() => {
    if (!dataByDevSrc) {
      return;
    }
    const devMap: any = {}

    const devArray = dataByDevSrc;
    for (let i = 0; i < devArray.length; i++) {
      const item: any = devArray[i];
      const ip = item.source_ip

      if (!ip) {
        return;
      }

      if (!devMap[ip]) {
        devMap[ip] = {
          ip,
          up_bytes: 0,
          dn_bytes: 0,
          sum_bytes: 0,
        };
      }

      const it = devMap[ip];
      it.sum_bytes += +item.sum_bytes_download + +item.sum_bytes_upload;
      it.dn_bytes += +item.sum_bytes_download;
      it.up_bytes += +item.sum_bytes_upload;
    }
    const devArraySorted = Object.values(devMap)
      .sort((a: any, b: any) => b.sum_bytes - a.sum_bytes)
      .slice(0, 10);
    setDataByDev(devArraySorted);
    console.log('data', dataByDev);
    console.log('devMap', devMap);
    console.log('devArray', devArraySorted);
    console.log('devArraySorted', devArraySorted);
    console.log('dataLANByPort', dataLanByPort);
    console.log('dataByDev', dataByDev);

  }, [dataByDevSrc]);

  useQuery({
    queryKey: ['report/interfaces', selectedId],
    queryFn: async () => {
      return axios
        .get('/api/report/interfaces' + apiPath)
    },
    onSuccess(response) {
      setInterfaces(response.data.interfaces)
    }
  })

  useQuery({
    queryKey: ['report/flows/asn', selectedId],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const paramsAsn = new URLSearchParams();

      paramsAsn.append('d', 'asn');
      paramsAsn.append('d', 'as_name');
      paramsAsn.append('m', 'sum_bytes_download');
      paramsAsn.append('m', 'sum_bytes_upload');
      paramsAsn.append('o', JSON.stringify({ take: 1000 }));

      return axios
        .get('/api/report/flows' + apiPath + '?' + paramsAsn.toString())

    },
    onSuccess(response) {
      setDataByAsn(response.data.result);
    }
  })

  useQuery({
    enabled: Boolean(lanInterfaces),
    queryKey: ['report/flows/devSrc', selectedId],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const paramsDevSrc = new URLSearchParams();

      paramsDevSrc.append('d', 'source_ip');
      paramsDevSrc.append('m', 'sum_bytes_download');
      paramsDevSrc.append('m', 'sum_bytes_upload');
      paramsDevSrc.append(
        'f',
        JSON.stringify({
          logic: 'OR',
          conditions: [
            {
              field: 'source_ip',
              operator: 'ip_in',
              value: '10.0.0.0/8',
            },
            {
              field: 'source_ip',
              operator: 'ip_in',
              value: '192.168.0.0/16',
            },
            {
              field: 'source_ip',
              operator: 'ip_in',
              value: '172.16.0.0/20',
            },
          ],
        }),
      );

      return axios
        .get('/api/report/flows' + apiPath + '?' + paramsDevSrc.toString())
    },
    onSuccess(response) {
      setDataByDevSrc(response.data.result);
    }
  })

  // useQuery({
  //   enabled: Boolean(lanInterfaces),
  //   queryKey: ['report/flows/devDst'],
  //   queryFn: async () => {
  //     const paramsDevDst = new URLSearchParams();

  //     paramsDevDst.append('d', 'destination_ip');
  //     paramsDevDst.append('m', 'sum_bytes_download');
  //     paramsDevDst.append('m', 'sum_bytes_upload');

  //     return axios
  //       .get('/api/report/flows/' + routerParams.ip + '?' + paramsDevDst.toString())
  //   },
  //   onSuccess(response) {
  //     setDataByDevDst(response.data.result);
  //   }
  // })

  useQuery({
    enabled: Boolean(lanInterfaces),
    queryKey: ['report/flows/p2', selectedId],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const p2 = new URLSearchParams();
      // Só `port`: o reduce abaixo agrupa exclusivamente por item.port, então
      // pedir source_interface e protocol junto só inflava a cardinalidade do
      // GROUP BY no Postgres (213 mil grupos medidos em produção, contra
      // algumas centenas de portas) para colunas que são descartadas aqui.
      p2.append('d', 'port');
      p2.append('m', 'sum_bytes_download');
      p2.append('m', 'sum_bytes_upload');

      return axios
        .get('/api/report/flows' + apiPath + '?' + p2.toString())
    },
    onSuccess(response) {
      const result = response.data.result;

      let totalBytes = 0;
      const _dataByPort = result.reduce(
        (acc: Record<string, any>, item: any) => {
          // -1 é o sentinela do rollup de portas (traffic-rollup-job.ts no
          // backend): portas com menos de alguns KB na janela, dobradas numa
          // linha só. Entra direto no "outros" do donut.
          const port = +item.port === -1 ? 'outros' : item.port;

          if (!port) {
            return acc;
          }

          if (!acc[port]) {
            acc[port] = {
              port,
              sum_bytes: 0,
              sum_bytes_download: 0,
              sum_bytes_upload: 0,
            };
          }

          const it = acc[port];
          it.sum_bytes += +item.sum_bytes_download + +item.sum_bytes_upload;
          it.sum_bytes_download += +item.sum_bytes_download;
          it.sum_bytes_upload += +item.sum_bytes_upload;

          totalBytes += +it.sum_bytes;

          return acc;
        },
        {},
      );

      for (const key in _dataByPort) {
        _dataByPort[key].percentage = +(
          (_dataByPort[key].sum_bytes / totalBytes) *
          100
        ).toFixed(1);
      }
      setDataLanByPort(topPortsWithOthers(Object.values(_dataByPort)));
    }
  })

  // Declarado por último de propósito: o backend serializa as queries de
  // relatório na ordem de chegada, e esta é a mais cara (4–18 s medidos,
  // contra 1–2 s das outras). Disparada depois, os três gráficos menores
  // aparecem antes em vez de esperarem atrás dela.
  useQuery({
    queryKey: ['report/flows', selectedId],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const params = new URLSearchParams();

      params.append('d', 'source_interface');
      params.append('d', 'destination_interface');
      params.append('d', 'datetime_bin');
      params.append('m', 'avg_kbps_upload');
      params.append('m', 'avg_kbps_download');
      // Bins de 15 min: no default (1 min) a janela de 24 h vira 1440 bins ×
      // pares de interface, e o LIMIT devolvia só as últimas horas do dia.
      // Medido em produção: ~62 pares por bin, então 96 bins precisam de
      // ~6000 linhas — 8000 dá folga. avg_kbps já divide pelo tamanho do bin,
      // então a escala do gráfico não muda.
      params.append('o', JSON.stringify({ histogram_minutes: 15, take: 8000 }));

      return axios
        .get('/api/report/flows' + apiPath + '?' + params.toString())
    },
    onSuccess(response) {
      setRawFlowData(response.data.result);
    }
  })


  return (
    <LayoutUsuario>
      <div className={styles.container}>
        <div className={styles.inner}>
          {/* Header */}
          <div className={styles.header}>
            <h2 className={styles.title}>Relatórios</h2>
            <select
              value={selectedId}
              onChange={e => {
                const val = e.target.value;
                setSelectedId(val);
                navigate(val ? `/relatorios/${val}` : '/relatorios', { replace: true });
              }}
              className={styles.select}
            >
              <option value="">— Todas as appliances —</option>
              {applianceOptions.map(opt => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Conteudo */}

          <div className={styles.chartBlock}>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={data} margin={{ right: 50 }}>
                <CartesianGrid stroke="var(--border-1)" strokeDasharray="3 3" />
                <XAxis
                  dataKey="datetime_bin"
                  tick={{ fill: 'var(--color-body)', fontSize: 12 }}
                  axisLine={{ stroke: 'var(--border-1)' }}
                  tickLine={{ stroke: 'var(--border-1)' }}
                  tickFormatter={tick =>
                    new Date(tick).toLocaleString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  }
                  interval={10}
                />
                <YAxis
                  tick={{ fill: 'var(--color-body)', fontSize: 12 }}
                  axisLine={{ stroke: 'var(--border-1)' }}
                  tickLine={{ stroke: 'var(--border-1)' }}
                  label={{
                    value: (() => {
                      const max = Math.max(...(data as any[]).flatMap((d: any) => [
                        d?.LAN?.download ?? 0, d?.LAN?.upload ?? 0,
                        d?.WAN?.download ?? 0, d?.WAN?.upload ?? 0,
                      ]));
                      return kbpsAxisUnit(max).unit;
                    })(), angle: -90, position: 'insideLeft', fill: 'var(--color-body)'
                  }}
                  tickFormatter={(tick: any) => {
                    const max = Math.max(...(data as any[]).flatMap((d: any) => [
                      d?.LAN?.download ?? 0, d?.LAN?.upload ?? 0,
                      d?.WAN?.download ?? 0, d?.WAN?.upload ?? 0,
                    ]));
                    const { divisor } = kbpsAxisUnit(max);
                    return (Math.abs(tick) / divisor).toFixed(divisor === 1 ? 0 : 2);
                  }}
                  domain={['auto', 'auto']}
                />
                <Tooltip
                  contentStyle={{
                    background: 'var(--surface-elevated)',
                    border: '1px solid var(--border-1)',
                    borderRadius: '12px',
                    boxShadow: 'var(--shadow-popover)',
                  }}
                  itemStyle={{ color: 'var(--color-body)' }}
                  labelStyle={{ color: 'var(--color-heading)' }}
                  labelFormatter={(value: string) =>
                    new Date(value).toLocaleString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  }
                  formatter={(value, name) => {
                    const absValue = Math.abs(value as number);
                    if (isNaN(absValue)) return ['0.00 kbps', name];
                    return [labelKbps(absValue), name];
                  }}
                />
                <Legend wrapperStyle={{ color: 'var(--color-body)' }} />
                <Line type="monotone" dataKey="LAN.download" name="LAN Download" stroke={chartColors[0]} strokeWidth={2.2} dot={{ r: 2.5, fill: chartColors[0], stroke: chartColors[0] }} activeDot={{ r: 4, fill: chartColors[0], stroke: 'var(--surface-card)' }} />
                <Line type="monotone" dataKey="LAN.upload" name="LAN Upload" stroke={chartColors[1]} strokeWidth={2.2} dot={{ r: 2.5, fill: chartColors[1], stroke: chartColors[1] }} activeDot={{ r: 4, fill: chartColors[1], stroke: 'var(--surface-card)' }} />
                <Line type="monotone" dataKey="WAN.download" name="WAN Download" stroke={chartColors[2]} strokeWidth={2.2} dot={{ r: 2.5, fill: chartColors[2], stroke: chartColors[2] }} activeDot={{ r: 4, fill: chartColors[2], stroke: 'var(--surface-card)' }} />
                <Line type="monotone" dataKey="WAN.upload" name="WAN Upload" stroke={chartColors[3]} strokeWidth={2.2} dot={{ r: 2.5, fill: chartColors[3], stroke: chartColors[3] }} activeDot={{ r: 4, fill: chartColors[3], stroke: 'var(--surface-card)' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Donut Charts */}
          <div className={styles.sectionRow}>
            <div className={styles.sectionPanel}>
              <div className={styles.panel}>
                <h4 className={styles.panelTitle}>LAN</h4>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart title='LAN'>
                    <Tooltip
                      contentStyle={{
                        background: 'var(--surface-elevated)',
                        border: '1px solid var(--border-1)',
                        borderRadius: '12px',
                        boxShadow: 'var(--shadow-popover)',
                      }}
                      itemStyle={{ color: 'var(--color-body)' }}
                      labelStyle={{ color: 'var(--color-heading)' }}
                      formatter={(value, name, props) => {
                        return [
                          `${props.payload.percentage}% (${labelBytes(+value)})`,
                          ports[name] || name,
                        ];
                      }}
                    />
                    <Pie
                      data={dataLanByPort}
                      dataKey="sum_bytes"
                      nameKey="port"
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={100}
                      fill="var(--color-data)"
                      labelLine={{ stroke: 'var(--border-1)' }}
                      label={({ name, percent, x, y, textAnchor }) =>
                        percent >= 0.02 ? (
                          <text x={x} y={y} fill="var(--color-body)" textAnchor={textAnchor} dominantBaseline="central" fontSize="11">
                            {`${ports[name] || name}: ${(percent * 100).toFixed(0)}%`}
                          </text>
                        ) : null
                      }
                      isAnimationActive={true}
                      animationBegin={0}
                      animationDuration={800}
                      animationEasing="ease-out"
                    >
                      {dataLanByPort.map((_entry: any, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={chartColors[index % chartColors.length]}
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

          </div>
          <div className={styles.sectionRow}>
            <div className={styles.sectionPanel}>
              <h4 className={styles.panelTitle}>Top 10 ASNs mais acessados</h4>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>ASN</th>
                      <th>Nome</th>
                      <th>Bytes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const asnArray = (dataByAsn as any[])
                        .map((item: any) => ({
                          asn: item.asn,
                          as_name: item.as_name,
                          sum_bytes: +item.sum_bytes_download + +item.sum_bytes_upload,
                        }))
                        .sort((a, b) => b.sum_bytes - a.sum_bytes)
                        .slice(0, 10);

                      return asnArray.map((item: any) => (
                        <tr key={item.asn ?? 'local'}>
                          <td>{item.asn ?? '(LOCAL)'}</td>
                          <td>{item.as_name ?? '(Tráfego inter-LAN)'}</td>
                          <td>{labelBytes(item.sum_bytes)}</td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className={styles.sectionRow}>
            <div className={styles.sectionPanel}>
              <h4 className={styles.panelTitle}>Top 10 dispositivos</h4>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>IP</th>
                      <th>Downloaded Bytes</th>
                      <th>Uploaded Bytes</th>
                      <th>Total Bytes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      return dataByDev.map((item: any) => (
                        <tr key={item.ip}>
                          <td>{item.ip ?? '??'}</td>
                          <td>{labelBytes(item.dn_bytes)}</td>
                          <td>{labelBytes(item.up_bytes)}</td>
                          <td>{labelBytes(item.sum_bytes)}</td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </LayoutUsuario>
  );
};

export default Page;
